import { DictType } from "@pma/types"
import { useState } from "react"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@components/common/command"
import { Popover, PopoverContent, PopoverTrigger } from "@components/common/popover"
import { cn } from "@pma/utils/helpers"
import { Button } from "@components/common/button"
import axios from "axios"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants"
import { useAuth } from "@stores/auth-store"
import { useMutation, useQuery } from "@tanstack/react-query"
import { SERVER_URL, STATUS_DICTIONARY_ENDPOINT } from "@pma/utils/api-routes"
import useTranslator from "@hooks/use-translator"
import { editTaskByCurrentUser } from "@pma/utils/utils"
import { toast } from "@pma/hooks/use-toast"
import AddStatusForm from "./add-status-form"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { CopyCheck } from "lucide-react"

interface Props {
  status?: DictType
  taskId?: Array<{ taskId: number; priorityId: number }>
  setStatus?: any
  priorityId?: number
}
const TaskBatchStatusEditor = ({ taskId }: Props) => {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState()

  const { authConfig } = useAuth()
  const { translate, locale } = useTranslator()

  const accessToken = parseCookies()[ACCESS_TOKEN]
  const orgId = authConfig?.last_organization_id
  const isHebrew = locale === "he"

  const getStatuses = async () => {
    const response = await axios.get(
      SERVER_URL +
        `${STATUS_DICTIONARY_ENDPOINT}?context_type=organization%20user&context_id=${orgId}&for_task=true&for_project=false`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const { data, refetch } = useQuery({
    queryKey: ["task-statuses", orgId],
    queryFn: getStatuses,
  })

  const updateTaskStatusMutation = useMutation({
    mutationKey: ["update-task-status"],
    mutationFn: async (statusId: number) => {
      if (Array.isArray(taskId)) {
        const data = Promise.all(
          taskId.map((val) =>
            editTaskByCurrentUser(val.taskId, {
              status_id: statusId,
              inform_users: true,
              priority_id: val.priorityId,
            }),
          ),
        )
          .then((values) => {
            if (value) {
              queryClient.invalidateQueries({ queryKey: ["get-tasks"] })
              queryClient.invalidateQueries({ queryKey: ["clear-selected-tasks"] })
              queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
              queryClient.invalidateQueries({ queryKey: ["clear-selected-group-tasks"] })
            }
            return values
          })
          .catch((error) => {
            const errorMessage = new Error(error.message)
            return errorMessage
          })
      }
    },
    onError: (error: DictType) => {
      const errorResponse = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorResponse || "Unable to perform this action"),
        variant: "error",
      })
    },
  })

  if (taskId) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <Tooltip>
          <TooltipTrigger>
            <PopoverTrigger asChild>
              <Button
                variant='outline'
                size='sm'
                className='ml-auto flex h-8 flex-row gap-2 lg:flex'
              >
                <CopyCheck size={14} />
                <span className='hidden min-w-[35px] flex-1 text-left sm:block'>
                  {`${translate("Change")} ${taskId.length === 1 ? translate("task's") : translate("tasks's")} ${translate("Status")}`}
                </span>
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent className='flex items-center gap-1 bg-destructive'>
            {<h4>{translate(`Change selected tasks's status`)}</h4>}
          </TooltipContent>
        </Tooltip>

        <PopoverContent className='w-[250px] p-0'>
          <Command className={isHebrew ? "rtl" : "ltr"}>
            <CommandInput placeholder={`${translate("Search")}...`} />
            <CommandList>
              <CommandEmpty>{translate("No status found")}.</CommandEmpty>
              <CommandGroup>
                {data?.map((status: DictType) => (
                  <CommandItem
                    key={status.id}
                    value={status?.name}
                    onSelect={(currentValue: any) => {
                      setValue(status?.name)
                      setOpen(false)
                      updateTaskStatusMutation.mutate(status?.id)
                    }}
                    className={cn(
                      "my-1 text-black",
                      status?.color === "#transparent" && "bg-primary",
                    )}
                    style={{ background: status?.color }}
                  >
                    {status.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          <div className='p-2'>
            <AddStatusForm option='task' refetch={refetch} />
          </div>{" "}
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='secondary'
          role='combobox'
          aria-expanded={open}
          className='h-8 w-full text-black'
          style={{
            background: data?.find((status: DictType) => status?.name === value)?.color,
          }}
        >
          {value
            ? data?.find((status: DictType) => status?.name === value)?.name
            : translate("Select Status")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[250px] p-0'>
        <Command className={isHebrew ? "rtl" : "ltr"}>
          <CommandInput placeholder={`${translate("Search")}...`} />
          <CommandList>
            <CommandEmpty>{translate("No status found")}.</CommandEmpty>
            <CommandGroup>
              {data?.map((status: DictType) => (
                <CommandItem
                  key={status.id}
                  value={status?.name}
                  onSelect={(currentValue: any) => {
                    setValue(status?.name)
                    setOpen(false)
                  }}
                  className={cn(
                    "my-1 text-white",
                    status?.color === "#transparent" && "bg-primary",
                  )}
                  style={{ background: status?.color }}
                >
                  {status.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
        <div className='p-2'>
          <AddStatusForm option='task' refetch={refetch} />
        </div>{" "}
      </PopoverContent>
    </Popover>
  )
}

export default TaskBatchStatusEditor
